import type { ApiErrorPayload } from '@/types/api'

/**
 * Thrown by services and ApiClient for every non-2xx response.
 *
 * Callers can narrow with `instanceof ApiError` to get status/code/payload
 * safely. All other throws that reach a service boundary are wrapped into
 * ApiError(status=0, code='NETWORK') by `ApiError.fromNetworkError`.
 *
 *   try {
 *     await startupService.list()
 *   } catch (err) {
 *     if (err instanceof ApiError && err.status === 401) {
 *       // redirect to /login
 *     }
 *   }
 */
export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly payload?: ApiErrorPayload
  readonly url?: string

  constructor(params: {
    status: number
    message: string
    code?: string
    payload?: ApiErrorPayload
    url?: string
  }) {
    super(params.message)
    this.name = 'ApiError'
    this.status = params.status
    this.code = params.code ?? ApiError.codeForStatus(params.status)
    this.payload = params.payload
    this.url = params.url
    // Restore prototype chain after extending built-in Error (TS quirk)
    Object.setPrototypeOf(this, ApiError.prototype)
  }

  /** Build an ApiError from a fetch Response whose `!ok` short-circuited. */
  static async fromResponse(response: Response): Promise<ApiError> {
    const payload = await ApiError.readJsonPayload(response)
    const message = ApiError.messageFromPayload(payload, response)
    return new ApiError({
      status: response.status,
      message,
      code: payload?.code,
      payload,
      url: response.url,
    })
  }

  /** Wrap an unknown thrown value (usually a fetch network error) as ApiError. */
  static fromNetworkError(err: unknown, url?: string): ApiError {
    const message =
      err instanceof Error ? err.message : 'Network request failed'
    return new ApiError({
      status: 0,
      code: 'NETWORK',
      message,
      url,
    })
  }

  /** True for statuses a retry-with-backoff is worth attempting. */
  get isRetriable(): boolean {
    if (this.status === 0) return true // network blip
    if (this.status === 408 || this.status === 429) return true
    if (this.status >= 500 && this.status < 600) return true
    return false
  }

  /** True for 401 — callers forward to auth flow (commit 04 adds the interceptor). */
  get isUnauthorized(): boolean {
    return this.status === 401
  }

  toJSON(): Record<string, unknown> {
    return {
      name: this.name,
      status: this.status,
      code: this.code,
      message: this.message,
      url: this.url,
      payload: this.payload,
    }
  }

  private static async readJsonPayload(
    response: Response
  ): Promise<ApiErrorPayload | undefined> {
    const contentType = response.headers.get('content-type') ?? ''
    if (!contentType.includes('application/json')) return undefined
    try {
      return (await response.json()) as ApiErrorPayload
    } catch {
      return undefined
    }
  }

  private static messageFromPayload(
    payload: ApiErrorPayload | undefined,
    response: Response
  ): string {
    if (payload?.message) return payload.message
    if (payload?.error) return payload.error
    return `HTTP ${response.status}: ${response.statusText || 'Request failed'}`
  }

  private static codeForStatus(status: number): string {
    if (status === 0) return 'NETWORK'
    if (status === 400) return 'BAD_REQUEST'
    if (status === 401) return 'UNAUTHORIZED'
    if (status === 403) return 'FORBIDDEN'
    if (status === 404) return 'NOT_FOUND'
    if (status === 409) return 'CONFLICT'
    if (status === 429) return 'RATE_LIMITED'
    if (status >= 500) return 'SERVER_ERROR'
    return 'HTTP_ERROR'
  }
}
